/**
 * Editing the register in the cell (`equipment-page-table`, R7–R10), shared by the Rows and Groups
 * views so both edit the same way: one cell open at a time (opening another blurs, and so commits,
 * the first), Tab from a Name to its Asset Identifier, F2 on a focused row to its Name.
 *
 * The answers are worked out against the twin's names with the edited row left out
 * (`nameIndexWithout`), and every commit goes through `useEquipmentRowWrites`.
 *
 * **Which cell is open lives in a small store, not in React state here**, and each cell subscribes
 * to it. So `editCell` keeps one identity for the life of the view, the table's columns keep theirs,
 * and opening, closing or tabbing between cells re-renders only the cells whose state changed —
 * not every cell in the table, which defeated the table's row memo at 2,000 rows (NFR1).
 */
import React, { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { registerRowName } from '@repo/front-end-utils';
import type { FloorPlannerLayout } from '@repo/frontend-types';
import type { TwinAssetResponse } from '@repo/front-end-services';

import EditableRegisterCell, { type CellAnswer } from './EditableRegisterCell';
import type { EditableRegisterField } from './equipmentRegisterColumns';
import {
  identifierAnswer,
  identifierAnswerCommits,
  nameAnswer,
  nameIndexWithout,
} from './equipmentRowEdit';
import {
  useEquipmentRowWrites,
  type CellWriteOutcome,
  type EquipmentWriteDeps,
} from './useEquipmentRowWrites';

interface OpenCell {
  rowId: number;
  field: EditableRegisterField;
}

/** The one open cell, and a version bumped when the register or plan changes under it. */
interface OpenCellStore {
  get: () => OpenCell | null;
  set: (next: OpenCell | null | ((current: OpenCell | null) => OpenCell | null)) => void;
  version: () => number;
  bump: () => void;
  subscribe: (listener: () => void) => () => void;
}

const createOpenCellStore = (): OpenCellStore => {
  let open: OpenCell | null = null;
  let version = 0;
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach(listener => listener());
  return {
    get: () => open,
    set: next => {
      const value = typeof next === 'function' ? next(open) : next;
      if (value === open) return;
      open = value;
      notify();
    },
    version: () => version,
    bump: () => {
      version += 1;
      notify();
    },
    subscribe: listener => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

/** What a cell reads at render: the latest rows, plan and writes, through one stable ref. */
interface EditorContext {
  store: OpenCellStore;
  rows: readonly TwinAssetResponse[];
  layout: FloorPlannerLayout | null | undefined;
  rename: (rowId: number, answer: ReturnType<typeof nameAnswer>) => Promise<CellWriteOutcome>;
  setIdentifier: (rowId: number, value: string | null) => Promise<CellWriteOutcome>;
}

/** F2 on a focused row edits its Name (R7): attach to the element wrapping the `Table`. */
export const editNameOnF2 = (event: React.KeyboardEvent<HTMLElement>) => {
  if (event.key !== 'F2') return;
  const target = event.target as HTMLElement;
  if (target.getAttribute('role') !== 'row') return;
  const button = target.querySelector<HTMLButtonElement>('[data-edit-field="name"]');
  if (!button) return;
  event.preventDefault();
  button.click();
};

/**
 * One editable cell. It re-renders when its OWN open state changes, and — while open — when the
 * register or plan the answer is checked against changes; a closed cell ignores both.
 */
const RegisterCellSlot: React.FC<{
  editor: React.MutableRefObject<EditorContext>;
  row: TwinAssetResponse;
  field: EditableRegisterField;
  sub: string | null;
}> = ({ editor, row, field, sub }) => {
  const { store } = editor.current;
  const snapshot = useSyncExternalStore(store.subscribe, () => {
    const open = store.get();
    return open !== null && open.rowId === row.id && open.field === field
      ? `open:${store.version()}`
      : 'closed';
  });
  const isOpen = snapshot !== 'closed';
  const { rows, layout, rename, setIdentifier } = editor.current;
  // The twin's names without this row, built while it is open — once, not per keystroke.
  const nameIndex = useMemo(
    () => (isOpen && field === 'name' ? nameIndexWithout(layout, rows, row.id) : null),
    // `snapshot` carries the version: a re-read while the cell is open rebuilds it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [snapshot, field, row.id],
  );
  const rowName = registerRowName(row);
  const answerFor = (typed: string): CellAnswer => {
    if (field === 'name') {
      const answer = nameAnswer(typed, row, nameIndex ?? nameIndexWithout(layout, rows, row.id));
      return {
        line: answer.line,
        refused: answer.kind === 'empty',
        unchanged: answer.kind === 'unchanged',
        saves: answer.name,
      };
    }
    const answer = identifierAnswer(typed, row, rows);
    return {
      line: answer.line,
      refused: answer.kind === 'taken',
      unchanged: !identifierAnswerCommits(answer) && answer.kind !== 'taken',
      saves: answer.value,
    };
  };
  return (
    <EditableRegisterCell
      field={field}
      value={field === 'name' ? rowName : (row.customer_asset_identifier ?? null)}
      sub={sub}
      rowName={rowName}
      rowId={row.id}
      open={isOpen}
      onOpen={() => store.set({ rowId: row.id, field })}
      onReopen={() => store.set(current => (current === null ? { rowId: row.id, field } : current))}
      onClose={() =>
        store.set(current =>
          current && current.rowId === row.id && current.field === field ? null : current,
        )
      }
      onTabOut={backwards => {
        if (field === 'name' && !backwards) store.set({ rowId: row.id, field: 'identifier' });
        if (field === 'identifier' && backwards) store.set({ rowId: row.id, field: 'name' });
      }}
      answerFor={answerFor}
      commit={typed =>
        field === 'name'
          ? rename(
              row.id,
              nameAnswer(typed, row, nameIndex ?? nameIndexWithout(layout, rows, row.id)),
            )
          : setIdentifier(row.id, identifierAnswer(typed, row, rows).value)
      }
    />
  );
};

export const useRegisterCellEditor = ({
  rows,
  layout,
  writes,
}: {
  rows: readonly TwinAssetResponse[];
  layout: FloorPlannerLayout | null | undefined;
  /** Absent: the cells are read-only, and `editCell` is undefined. */
  writes: EquipmentWriteDeps | undefined;
}) => {
  const store = useMemo(createOpenCellStore, []);
  const { rename, setIdentifier, add } = useEquipmentRowWrites(writes ?? READ_ONLY);
  const editor = useRef<EditorContext>({ store, rows, layout, rename, setIdentifier });
  editor.current = { store, rows, layout, rename, setIdentifier };
  // A re-read or a plan change re-renders the open cell (its answer is checked against both).
  useEffect(() => {
    store.bump();
  }, [store, rows, layout]);

  // One identity for the life of the view: the table's columns, and its row memo, hold.
  const editCell = useCallback(
    (row: TwinAssetResponse, field: EditableRegisterField, sub: string | null): React.ReactNode => (
      <RegisterCellSlot editor={editor} row={row} field={field} sub={sub} />
    ),
    [],
  );

  return { editCell: writes ? editCell : undefined, add: writes ? add : undefined };
};

/** Never called: the hook needs deps to be called unconditionally, and no cell renders without writes. */
const READ_ONLY: EquipmentWriteDeps = {
  twinId: null,
  currentTwinId: () => null,
  baseUrl: '',
  getToken: () => Promise.reject(new Error('read-only')),
  begin: () => 0,
  settle: () => undefined,
  reload: () => undefined,
  discard: () => Promise.reject(new Error('read-only')),
  create: () => Promise.reject(new Error('read-only')),
  forgetWrites: () => undefined,
  registerIds: () => new Set(),
  removable: () => [],
  onRetryRemoved: () => undefined,
};
